// =============================================================================
// Devolución de combos — el combo es una unidad indivisible.
// =============================================================================
// En `sale_items` un combo ocupa varias filas:
//   - una fila `combo_role='parent'` con el producto combo y el PRECIO de la
//     promo (ej. PROMO X 6 EMPANADAS, 1 × $7200),
//   - N filas `combo_role='component'` con los productos que salieron de stock
//     y su revenue PRORRATEADO, que suma exactamente lo mismo que el padre
//     (7 componentes que suman $7200 en la venta real 002dcbba).
//
// Los componentes se asocian al padre por `parent_combo_product_id` (apunta al
// PRODUCTO combo, no a la fila).
//
// Dos bugs que resuelve este módulo:
//
// 1. DINERO: el total de la devolución sumaba todas las filas, así que devolver
//    la promo entera reintegraba $14400 en vez de $7200 — exactamente el doble.
//    La plata la define la fila padre; los componentes son detalle interno.
//
// 2. STOCK: la fila padre no reintegra stock (nunca se descontó, ver
//    utils/comboStock.js), así que al devolver la promo no volvía nada. Lo que
//    tiene que volver son sus componentes, en proporción a los combos devueltos.
//
// Regla de negocio: el combo se devuelve por unidades de combo. Los componentes
// no son seleccionables por separado.

/**
 * Líneas que el operador puede elegir en una devolución de venta.
 *
 * Devuelve las filas "devolvibles" —producto suelto (`combo_role` nulo) o combo
 * (`combo_role='parent'`)— y cuelga de cada combo sus componentes como detalle
 * informativo. Las filas `component` NUNCA son líneas propias: mostrarlas como
 * seleccionables es lo que producía el doble cobro.
 *
 * @param {Array<object>} saleItems filas de sale_items de la venta
 * @returns {Array<object>} líneas devolvibles; las de combo llevan
 *   `componentes: Array<object>` (vacío en combos legacy sin filas component)
 */
export function construirLineasDevolucion(saleItems) {
  const items = saleItems || [];

  // Devolvible: producto suelto (combo_role nulo/vacío) o fila padre de combo.
  // Las filas component NUNCA son líneas propias.
  const esDevolvible = (it) => {
    const role = String(it?.combo_role || '');
    return role === '' || role === 'parent';
  };

  return items.filter(esDevolvible).map((it) => {
    const esParent = String(it?.combo_role || '') === 'parent';
    const componentes = esParent
      ? items.filter(
          (c) => String(c?.combo_role || '') === 'component' && c?.parent_combo_product_id === it.product_id
        )
      : [];

    return { ...it, componentes };
  });
}

/**
 * Total en pesos de la devolución.
 *
 * Suma `cantidad × unit_price` SOLO de las líneas devolvibles. Las filas
 * `component` se excluyen: su revenue ya está contado en el precio del padre.
 *
 * @param {{saleItems?: Array<object>, cantidadPorItemId?: Record<string, number>}} args
 * @returns {number}
 */
export function calcularTotalDevolucion(args) {
  const { saleItems, cantidadPorItemId } = args || {};
  const cantidades = cantidadPorItemId || {};

  return construirLineasDevolucion(saleItems).reduce((acc, linea) => {
    const cantidad = Number(cantidades[linea.id]) || 0;
    return acc + cantidad * Number(linea.unit_price || 0);
  }, 0);
}

/**
 * Ítems a reintegrar en stock, con los combos ya expandidos a sus componentes.
 *
 * - Producto suelto → se reintegra él mismo, por la cantidad devuelta.
 * - Combo → se reintegran sus componentes, prorrateados por la fracción de
 *   combos devueltos: `cantidad_componente × (devuelto / vendido)`. Si de una
 *   promo de 6 empanadas (componente 1.070) se devuelve la mitad, vuelven 0.535.
 * - Combo legacy sin filas `component` → no aporta stock: el descuento original
 *   fue de ingredientes que no quedaron registrados y no se puede reconstruir.
 *
 * Cuando el mismo producto combo aparece en varias filas padre de la misma
 * venta, se prorratea sobre el TOTAL de esas filas: los componentes están
 * cargados una sola vez para todas.
 *
 * El resultado se puede pasar tal cual a `buildRestorableQuantityMap` de
 * utils/comboStock.js, que agrega por producto.
 *
 * @param {{saleItems?: Array<object>, cantidadPorItemId?: Record<string, number>}} args
 * @returns {Array<{product_id: string, quantity: number}>}
 */
export function expandirItemsParaStock(args) {
  const { saleItems, cantidadPorItemId } = args || {};
  const cantidades = cantidadPorItemId || {};
  const lineas = construirLineasDevolucion(saleItems);

  const resultado = [];
  // Un mismo producto-combo puede repetir fila padre en la venta (12 casos en
  // producción): los componentes están cargados una sola vez para todas esas
  // filas, así que el prorrateo se hace una única vez por producto-combo, no
  // por fila, para no duplicar el reintegro.
  const comboProductosExpandidos = new Set();

  lineas.forEach((linea) => {
    const devuelto = Number(cantidades[linea.id]) || 0;
    if (devuelto <= 0) return;

    const esParent = String(linea.combo_role || '') === 'parent';

    if (!esParent) {
      // Producto suelto: se reintegra él mismo, por la cantidad devuelta.
      resultado.push({ product_id: linea.product_id, quantity: devuelto });
      return;
    }

    // Combo legacy sin filas component: el stock descontado (ingredientes) no
    // quedó registrado, no se puede reconstruir, no aporta reintegro.
    if (linea.componentes.length === 0) return;

    const comboProductId = linea.product_id;
    if (comboProductosExpandidos.has(comboProductId)) return;
    comboProductosExpandidos.add(comboProductId);

    // Prorrateo sobre el TOTAL vendido/devuelto de ese producto-combo,
    // sumando todas sus filas padre (no solo esta).
    const filasPadreMismoProducto = lineas.filter(
      (l) => String(l.combo_role || '') === 'parent' && l.product_id === comboProductId
    );
    const vendidoTotal = filasPadreMismoProducto.reduce((acc, l) => acc + Number(l.quantity || 0), 0);
    const devueltoTotal = filasPadreMismoProducto.reduce(
      (acc, l) => acc + (Number(cantidades[l.id]) || 0),
      0
    );

    if (vendidoTotal <= 0) return;

    // Topeado en 1: devolver más de lo vendido no infla el reintegro.
    const factor = Math.min(1, devueltoTotal / vendidoTotal);

    linea.componentes.forEach((componente) => {
      resultado.push({
        product_id: componente.product_id,
        quantity: Number(componente.quantity || 0) * factor,
      });
    });
  });

  return resultado;
}
